/**
 * GIF search: a provider-agnostic pagination state machine (debounced queries, stale responses dropped, "load more",
 * retry, "unavailable"), a client-side request budget, and the KLIPY adapter (request URLs and response parsing).
 *
 * KLIPY's terms (docs.klipy.com, Integration Requirements, 2026-10-16): requests go from the user's client straight to
 * api.klipy.com, media load from the returned static.klipy.com URLs unchanged (never stored or re-hosted), results
 * keep KLIPY's order in their own GIF tab, the search field says "Search KLIPY", and filtering is `content_filter`
 * (off/low/medium/high) plus the Partner Panel. A sent GIF triggers the share ping. If a server proxy is ever approved,
 * only the adapter changes: the state machine and the picker stay.
 */
import type { GifContentFilter, GifProvider, GifRef } from "@bundle/contracts";
import { fitWithin } from "./files.ts";

/** One result in the GIF grid: what a message will carry, plus a smaller animated thumbnail for the grid. */
export type GifResult = { ref: GifRef; thumbUrl: string; thumbWidth: number; thumbHeight: number };

/** A page of results; `nextCursor` absent = the end. */
export type GifPage = { items: GifResult[]; nextCursor?: string };

/**
 * "unavailable": no provider (no key on the server, or GIFs off for the workspace). "idle": not searched yet.
 * "loading": first page in flight; "loadingMore": next page in flight.
 */
export type GifStatus = "unavailable" | "idle" | "loading" | "ready" | "loadingMore" | "error";
export type GifError = "network" | "rateLimited" | "server";

export type GifSearchState = {
  status: GifStatus;
  /** The query being shown ("" = trending). */
  query: string;
  items: GifResult[];
  nextCursor?: string;
  /** Increases with every request; responses for older ids are ignored. */
  requestId: number;
  error?: GifError;
};

export type GifEvent =
  | { type: "available"; available: boolean }
  /** A new (debounced) query; "" = trending. */
  | { type: "search"; query: string }
  /** The grid reached its end. */
  | { type: "more" }
  | { type: "loaded"; requestId: number; page: GifPage }
  | { type: "failed"; requestId: number; error: GifError }
  | { type: "retry" };

/** What to fetch next (status loading/loadingMore). */
export type GifRequest = { requestId: number; query: string; cursor?: string };

export const GIF_PAGE_SIZE = 24;
export const GIF_DEBOUNCE_MS = 350;
export const MAX_GIF_QUERY = 50;
/** Client-side budget per person and device (KLIPY test keys allow 100 requests/hour for a whole app). */
export const GIF_REQUESTS_PER_MINUTE = 20;
export const GIF_RATE_WINDOW_MS = 60_000;

export function initialGifState(): GifSearchState {
  return { status: "idle", query: "", items: [], requestId: 0 };
}

function normalizeQuery(q: string): string {
  return q.trim().replace(/\s+/g, " ").slice(0, MAX_GIF_QUERY);
}

function withoutError(s: GifSearchState): GifSearchState {
  const { error: _e, ...rest } = s;
  return rest;
}

export function gifReducer(s: GifSearchState, e: GifEvent): GifSearchState {
  switch (e.type) {
    case "available": {
      if (!e.available) return { status: "unavailable", query: s.query, items: [], requestId: s.requestId + 1 };
      return s.status === "unavailable" ? { status: "idle", query: s.query, items: [], requestId: s.requestId } : s;
    }
    case "search": {
      if (s.status === "unavailable") return s;
      const query = normalizeQuery(e.query);
      if (query === s.query && s.status !== "idle" && s.status !== "error") return s;
      return { status: "loading", query, items: [], requestId: s.requestId + 1 };
    }
    case "more": {
      if (s.status !== "ready" || s.nextCursor === undefined) return s;
      return { ...withoutError(s), status: "loadingMore", requestId: s.requestId + 1 };
    }
    case "retry": {
      if (s.status !== "error") return s;
      const next = withoutError(s);
      return { ...next, status: s.items.length > 0 && s.nextCursor !== undefined ? "loadingMore" : "loading", requestId: s.requestId + 1 };
    }
    case "loaded": {
      if (e.requestId !== s.requestId || (s.status !== "loading" && s.status !== "loadingMore")) return s;
      const seen = new Set(s.status === "loadingMore" ? s.items.map((i) => i.ref.id) : []);
      const fresh = e.page.items.filter((i) => {
        if (seen.has(i.ref.id)) return false;
        seen.add(i.ref.id);
        return true;
      });
      const next: GifSearchState = {
        status: "ready",
        query: s.query,
        items: s.status === "loadingMore" ? [...s.items, ...fresh] : fresh,
        requestId: s.requestId,
      };
      if (e.page.nextCursor !== undefined) next.nextCursor = e.page.nextCursor;
      return next;
    }
    case "failed": {
      if (e.requestId !== s.requestId || (s.status !== "loading" && s.status !== "loadingMore")) return s;
      return { ...s, status: "error", error: e.error };
    }
  }
}

/** The request the state is waiting for, if any. */
export function pendingGifRequest(s: GifSearchState): GifRequest | undefined {
  if (s.status === "loading") return { requestId: s.requestId, query: s.query };
  if (s.status === "loadingMore" && s.nextCursor !== undefined) return { requestId: s.requestId, query: s.query, cursor: s.nextCursor };
  return undefined;
}

/** Sliding-window budget: may another request go out now? `sent` = earlier request times (any order). */
export function allowGifRequest(sent: number[], now: number, limit: number, windowMs: number): boolean {
  let recent = 0;
  for (const t of sent) if (t > now - windowMs && t <= now) recent += 1;
  return recent < limit;
}

/** Request times still inside the window (to keep the list short). */
export function pruneGifRequests(sent: number[], now: number, windowMs: number): number[] {
  return sent.filter((t) => t > now - windowMs && t <= now);
}

/** Size of a GIF in a message. */
export function gifDisplaySize(ref: { width: number; height: number }, maxWidth: number, maxHeight: number): { width: number; height: number } {
  return fitWithin(ref.width, ref.height, maxWidth, maxHeight);
}

// ---------- Providers ----------

/** Media hosts each provider serves from (GIF URLs in messages must be https on one of these). */
export function gifMediaHosts(provider: GifProvider): string[] {
  switch (provider) {
    case "klipy":
      return ["static.klipy.com"];
  }
}

/** https on the provider's media host, without credentials or an explicit port. */
export function isAllowedGifUrl(provider: GifProvider, url: string): boolean {
  const m = /^https:\/\/([a-z0-9.-]+)(\/[^\s]*)?$/i.exec(url);
  if (m === null) return false;
  return gifMediaHosts(provider).includes(m[1]!.toLowerCase());
}

/** Why a GIF can't be posted (server check), or null. */
export function gifRefProblem(ref: GifRef): string | null {
  if (!isAllowedGifUrl(ref.provider, ref.url)) return "GIF url must be https on the provider's media host";
  if (ref.stillUrl !== undefined && !isAllowedGifUrl(ref.provider, ref.stillUrl)) return "GIF stillUrl must be https on the provider's media host";
  if (!/^image\/(gif|webp)$|^video\/(mp4|webm)$/.test(ref.mime)) return "GIF mime must be image/gif, image/webp, video/mp4 or video/webm";
  return null;
}

// ---------- KLIPY ----------

export const KLIPY_API_BASE = "https://api.klipy.com/api/v1";

export type KlipyConfig = { apiKey: string; contentFilter: GifContentFilter; customerId?: string };

/** KLIPY wants an ISO 3166 alpha-2 country: "pt-BR" → "br", "en" → "us", "en-GB" → "gb". */
export function klipyLocale(locale: string): string {
  const parts = locale.split(/[-_]/);
  const region = parts.length > 1 ? parts[parts.length - 1]! : "";
  if (/^[a-z]{2}$/i.test(region)) return region.toLowerCase();
  return parts[0]!.toLowerCase() === "pt" ? "br" : "us";
}

/** Search (non-empty query) or trending ("") URL for one page; `cursor` = the page number from the previous page. */
export function klipyListUrl(config: KlipyConfig, query: string, cursor: string | undefined, perPage: number, locale: string): string {
  const q = normalizeQuery(query);
  const page = cursor === undefined ? "1" : cursor;
  const params: [string, string][] = [
    ["page", page],
    ["per_page", String(perPage)],
  ];
  if (q !== "") params.push(["q", q]);
  if (config.customerId !== undefined) params.push(["customer_id", config.customerId]);
  params.push(["locale", klipyLocale(locale)]);
  params.push(["content_filter", config.contentFilter]);
  params.push(["format_filter", "gif,webp,jpg"]);
  const search = params.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return `${KLIPY_API_BASE}/${encodeURIComponent(config.apiKey)}/gifs/${q === "" ? "trending" : "search"}?${search}`;
}

/** The share ping for a sent GIF (POST, JSON body from `klipyShareBody`). */
export function klipyShareUrl(config: KlipyConfig, slug: string): string {
  return `${KLIPY_API_BASE}/${encodeURIComponent(config.apiKey)}/gifs/share/${encodeURIComponent(slug)}`;
}

/** `q` = the search that led to the share ("" from trending). */
export function klipyShareBody(config: KlipyConfig, query: string): Record<string, string> {
  const body: Record<string, string> = { q: normalizeQuery(query) };
  if (config.customerId !== undefined) body.customer_id = config.customerId;
  return body;
}

type KlipyRendition = { url?: unknown; width?: unknown; height?: unknown };
type KlipySize = { gif?: KlipyRendition; webp?: KlipyRendition; jpg?: KlipyRendition };

function rendition(r: KlipyRendition | undefined, mime: string): { url: string; width: number; height: number; mime: string } | undefined {
  if (r === undefined || r === null || typeof r !== "object") return undefined;
  if (typeof r.url !== "string" || typeof r.width !== "number" || typeof r.height !== "number") return undefined;
  if (r.width <= 0 || r.height <= 0 || !isAllowedGifUrl("klipy", r.url)) return undefined;
  return { url: r.url, width: Math.round(r.width), height: Math.round(r.height), mime };
}

function firstOf(sizes: Record<string, KlipySize | undefined>, order: [string, "webp" | "gif" | "jpg"][]) {
  for (const [size, format] of order) {
    const s = sizes[size];
    const r = s === undefined || s === null ? undefined : rendition(s[format], format === "jpg" ? "image/jpeg" : `image/${format}`);
    if (r !== undefined) return r;
  }
  return undefined;
}

/**
 * KLIPY list response (`{ result, data: { data: [...], current_page, has_next } }`) → a page, in KLIPY's order. Items
 * without a usable animated rendition and ad objects (`type: "ad"`; ads stay off for Bundle's key) are skipped.
 * Messages use the "hd" WebP (498 px), the grid the "sm" WebP (220 px), the still is the "sm" JPEG.
 */
export function parseKlipyPage(json: unknown): GifPage {
  const root = (json ?? {}) as { data?: { data?: unknown; current_page?: unknown; has_next?: unknown } };
  const data = root.data ?? {};
  const list = Array.isArray(data.data) ? (data.data as unknown[]) : [];
  const items: GifResult[] = [];
  for (const raw of list) {
    const it = (raw ?? {}) as { id?: unknown; slug?: unknown; title?: unknown; type?: unknown; file?: unknown };
    if (it.type !== undefined && it.type !== "gif") continue;
    const slug = typeof it.slug === "string" && it.slug !== "" ? it.slug : typeof it.id === "number" || typeof it.id === "string" ? String(it.id) : "";
    if (slug === "") continue;
    const sizes = (it.file ?? {}) as Record<string, KlipySize | undefined>;
    const main = firstOf(sizes, [["hd", "webp"], ["md", "webp"], ["sm", "webp"], ["hd", "gif"], ["md", "gif"], ["sm", "gif"]]);
    const thumb = firstOf(sizes, [["sm", "webp"], ["xs", "webp"], ["sm", "gif"], ["xs", "gif"], ["md", "webp"], ["hd", "webp"]]);
    if (main === undefined || thumb === undefined) continue;
    const still = firstOf(sizes, [["sm", "jpg"], ["md", "jpg"], ["hd", "jpg"], ["xs", "jpg"]]);
    const ref: GifRef = {
      provider: "klipy",
      id: slug.slice(0, 200),
      title: typeof it.title === "string" ? it.title.slice(0, 300) : "",
      url: main.url,
      mime: main.mime,
      width: main.width,
      height: main.height,
    };
    if (still !== undefined) ref.stillUrl = still.url;
    items.push({ ref, thumbUrl: thumb.url, thumbWidth: thumb.width, thumbHeight: thumb.height });
  }
  const page: GifPage = { items };
  const current = typeof data.current_page === "number" ? data.current_page : Number(data.current_page);
  if (data.has_next === true && Number.isFinite(current)) page.nextCursor = String(current + 1);
  return page;
}
